import { supabase } from '@/lib/supabase';
import type { OAuthInitResponse } from '@shared/types/integrations';

export const OAUTH_POLL_INTERVAL_MS = 2000;
export const OAUTH_POLL_TIMEOUT_MS = 120000;

type OAuthWindowMode = 'popup' | 'new_tab' | 'same_tab';

interface OAuthIntegrationRow {
  id: string;
  config: Record<string, unknown> | null;
}

export interface OAuthInitSuccess {
  url: string;
  attemptId: string;
  payload: OAuthInitResponse | Record<string, unknown> | null;
}

export interface OAuthWindowOpenResult {
  mode: OAuthWindowMode;
  shouldPoll: boolean;
}

export interface OAuthPollTick {
  attempts: number;
  elapsedMs: number;
}

export interface OAuthPollResult {
  connected: boolean;
  timedOut: boolean;
  aborted: boolean;
}

export const initOAuthConnection = async (provider: string): Promise<OAuthInitSuccess> => {
  const { data, error } = await supabase.functions.invoke('oauth-init', {
    body: { provider },
  });

  if (error) {
    throw new Error(error.message || `Failed to initialize OAuth for ${provider}`);
  }

  if (isOAuthInitFailure(data)) {
    const err = new Error(formatOAuthInitFailureMessage(data, provider));
    throw Object.assign(err, { payload: data });
  }

  const url = getOAuthAuthorizationUrl(data);
  if (!url) {
    const err = new Error('OAuth init response missing authorization URL');
    throw Object.assign(err, { payload: data });
  }
  const attemptId = isRecord(data) ? data.attemptId : null;
  if (typeof attemptId !== 'string' || !attemptId) {
    throw new Error('OAuth init response missing attempt ID');
  }

  return {
    url,
    attemptId,
    payload: isRecord(data) ? (data as Record<string, unknown>) : null,
  };
};

export const openOAuthWindow = (url: string): OAuthWindowOpenResult => {
  const popup = window.open(url, 'oauth', 'width=600,height=700,popup=yes');
  if (popup) {
    return { mode: 'popup', shouldPoll: true };
  }

  const newTab = window.open(url, '_blank', 'noopener,noreferrer');
  if (newTab) {
    return { mode: 'new_tab', shouldPoll: true };
  }

  window.location.assign(url);
  return { mode: 'same_tab', shouldPoll: false };
};

export const pollForOAuthConnection = async ({
  provider,
  attemptId,
  intervalMs = OAUTH_POLL_INTERVAL_MS,
  timeoutMs = OAUTH_POLL_TIMEOUT_MS,
  signal,
  onTick,
}: {
  provider: string;
  attemptId: string;
  intervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
  onTick?: (tick: OAuthPollTick) => void;
}): Promise<OAuthPollResult> => {
  const startedAt = Date.now();
  let attempts = 0;

  while (Date.now() - startedAt < timeoutMs) {
    if (signal?.aborted) {
      return { connected: false, timedOut: false, aborted: true };
    }

    const connected = await hasConnectedOAuthIntegration(provider, attemptId);
    if (connected) {
      return { connected: true, timedOut: false, aborted: false };
    }

    attempts += 1;
    onTick?.({
      attempts,
      elapsedMs: Date.now() - startedAt,
    });

    try {
      await sleep(intervalMs, signal);
    } catch (error) {
      if (isAbortError(error)) {
        return { connected: false, timedOut: false, aborted: true };
      }
      throw error;
    }
  }

  return { connected: false, timedOut: true, aborted: false };
};

export const getOAuthAuthorizationUrl = (payload: unknown): string | null => {
  if (!isRecord(payload)) return null;
  const directUrl = payload.url;
  if (typeof directUrl === 'string' && directUrl.length > 0) return directUrl;
  return null;
};

const hasConnectedOAuthIntegration = async (provider: string, attemptId: string): Promise<boolean> => {
  const { data, error } = await supabase
    .from('integrations')
    .select('id, config')
    .eq('provider', provider)
    .eq('is_active', true)
    .order('updated_at', { ascending: false })
    .limit(1);

  if (error) {
    throw new Error(error.message || `Failed to check ${provider} connection status`);
  }

  const row = (data?.[0] ?? null) as OAuthIntegrationRow | null;
  if (!row) return false;

  return row.config?.oauth_attempt_id === attemptId;
};

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError());
      return;
    }
    const timer = window.setTimeout(() => {
      cleanup();
      resolve();
    }, ms);

    const onAbort = () => {
      cleanup();
      reject(abortError());
    };

    const cleanup = () => {
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    };

    if (signal) {
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });

const abortError = (): Error => {
  try {
    return new DOMException('Aborted', 'AbortError');
  } catch {
    const error = new Error('Aborted');
    error.name = 'AbortError';
    return error;
  }
};

const isAbortError = (error: unknown): boolean =>
  isRecord(error) && error.name === 'AbortError';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isOAuthInitFailure = (payload: unknown): payload is OAuthInitResponse =>
  isRecord(payload) && payload.success === false;

const formatOAuthInitFailureMessage = (payload: OAuthInitResponse, provider: string): string => {
  if (Array.isArray(payload.missingEnvVars) && payload.missingEnvVars.length > 0) {
    const providerLabel = payload.providerLabel ?? provider;
    return `${providerLabel}: configuration OAuth serveur incomplète (${payload.missingEnvVars.join(', ')}). Vérifiez l'env des Edge Functions et redémarrez "npx supabase functions serve --env-file .env".`;
  }

  if (payload.errorCode === 'OAUTH_REDIRECT_URI_INVALID') {
    const providerLabel = payload.providerLabel ?? provider;
    return `${providerLabel}: callback OAuth invalide. Vérifiez OAUTH_REDIRECT_URI puis redémarrez les Edge Functions.`;
  }

  return payload.error || `Failed to initialize OAuth for ${provider}`;
};
